const { getOffsetTop } = window.BAO.Utils

class ProductFinder extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY
    this.currentVisibleQuestions = {}

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)
    this.updateControls = this.updateControls.bind(this)
    this.resetQuiz = this.resetQuiz.bind(this)

    this.updateEl()
    this.setupListeners()

    this.updateControls(false)
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.onOptionClick = this.onOptionClick.bind(this)
    this.handleTrigger = this.handleTrigger.bind(this)
    this.handleRestartClick = this.handleRestartClick.bind(this)

    window.addEventListener('scroll', this.onScroll, { passive: true })

    this.els.option.elements.forEach((option) => {
      option.addEventListener('click', this.onOptionClick)
    })

    this.listeners.add(document, 'click', this.handleTrigger)

    this.els.restart.element.addEventListener('click', this.handleRestartClick)
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.setOverlayOpacity()
  }

  updateControls (showControls = false) {
    const controlsElement = this.els.controls.element

    if (showControls) {
      controlsElement.setAttribute('aria-hidden', 'false')
    } else {
      controlsElement.setAttribute('aria-hidden', 'true')
    }
  }

  resetQuiz () {
    this.updateControls(false)
    this.currentVisibleQuestions = {}

    const firstQuestionElement = this.els.question.elements[0]
    if (firstQuestionElement) {
      this.scrollToElement(firstQuestionElement).then(() => {
        this.els.question.elements.forEach((question) => {
          question.setAttribute('aria-hidden', 'true')
        })

        this.els.option.elements.forEach((option) => {
          option.setAttribute('aria-pressed', 'false')
        })

        if (this.els.results.element) {
          this.els.results.element.setAttribute('aria-hidden', 'true')
        }

        firstQuestionElement.setAttribute('aria-hidden', 'false')
      })
    }
  }

  scrollToElement (element) {
    return new Promise((resolve) => {
      const targetY = element.getBoundingClientRect().top + window.scrollY

      element.scrollIntoView({ behavior: 'smooth' })

      const onScroll = () => {
        if (window.scrollY === targetY) {
          window.removeEventListener('scroll', onScroll)
          resolve()
        }
      }

      window.addEventListener('scroll', onScroll)
    })
  }

  handleRestartClick () {
    this.resetQuiz()
  }

  setOverlayOpacity () {
    if (!this.offsetHeight) return

    const maxOpacity = 0.8

    const elementStartPoint = getOffsetTop(this)
    const elementEndPoint = getOffsetTop(this) + window.innerHeight

    if (this.lastKnownY <= elementStartPoint) {
      this.els.overlay.element.style.setProperty('--Overlay', '0')
      return
    }

    if (this.lastKnownY > elementEndPoint) {
      this.els.overlay.element.style.setProperty('--Overlay', maxOpacity)
      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint
    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount
    const opacityAmount = maxOpacity * scrollYAsAPercentageOfJourney

    this.els.overlay.element.style.setProperty('--Overlay', opacityAmount)
  }

  onOptionClick (event) {
    const currentQuestionElement = event.currentTarget.closest(
      '[data-product-finder-el="question"]',
    )
    const currentQuestionId =
      currentQuestionElement.getAttribute('data-question')
    const nextQuestionId =
      event.currentTarget.getAttribute('data-next-question')

    if (nextQuestionId) {
      const nextQuestionElement = this.els.question.elements.find(
        (el) => el.getAttribute('data-question') === nextQuestionId,
      )

      if (nextQuestionElement) {
        nextQuestionElement.scrollIntoView({ behavior: 'smooth' })

        this.resetSubsequentQuestions(currentQuestionId)

        nextQuestionElement.setAttribute('aria-hidden', 'false')

        this.currentVisibleQuestions = {
          ...this.currentVisibleQuestions,
          [currentQuestionId]: nextQuestionElement,
        }
      }
    }

    const options = this.els.option.elements.filter(
      (option) =>
        option.closest('[data-product-finder-el="question"]') ===
        currentQuestionElement,
    )

    options.forEach((option) => {
      option.setAttribute('aria-pressed', 'false')
    })

    event.currentTarget.setAttribute('aria-pressed', 'true')

    if (!this.currentVisibleQuestions.firstQuestionAnswered) {
      this.updateControls(true)
      this.currentVisibleQuestions.firstQuestionAnswered = true
    }
  }

  resetSubsequentQuestions (currentQuestionId) {
    let reset = false
    this.els.question.elements.forEach((question) => {
      const questionId = question.getAttribute('data-question')

      if (reset || questionId === currentQuestionId) {
        reset = true

        if (this.currentVisibleQuestions[questionId]) {
          this.currentVisibleQuestions[questionId].setAttribute(
            'aria-hidden',
            'true',
          )

          delete this.currentVisibleQuestions[questionId]
        }
      }
    })
  }

  scrollToElementWithPadding (element, padding) {
    const rect = element.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const targetPosition = rect.top + scrollTop - padding

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })
  }

  async handleTrigger (event) {
    const trigger = event.target.closest('[data-product-finder-el="trigger"]')
    if (!trigger) return

    await window.BAO.sectionRenderer
      .fetchSingle('product-finder-results', trigger.dataset.collectionUrl)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('.shopify-section')

        this.els.products.element.innerHTML = myself.innerHTML

        this.els.results.element.setAttribute('aria-hidden', false)

        const element = this.els.results.element
        const padding = 200
        this.scrollToElementWithPadding(element, padding)
      })
      .catch(() => {})
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinder)
}
